"use client";

import { useState, useRef, useEffect, Fragment } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const LAYERS = [
    {
        key: "ctrl",
        icon: "routing-bold-duotone",
        nameKey: "Mvc_inaction_ctrl",
        roleKey: "Mvc_inaction_ctrl_role",
        tile: "bg-amber-500/15 text-amber-300",
        ring: "ring-amber-400/60",
        check: "text-amber-300",
    },
    {
        key: "model",
        icon: "database-bold-duotone",
        nameKey: "Mvc_inaction_model",
        roleKey: "Mvc_inaction_model_role",
        tile: "bg-sky-500/15 text-sky-300",
        ring: "ring-sky-400/60",
        check: "text-sky-300",
    },
    {
        key: "view",
        icon: "window-frame-bold-duotone",
        nameKey: "Mvc_inaction_view",
        roleKey: "Mvc_inaction_view_role",
        tile: "bg-emerald-500/15 text-emerald-300",
        ring: "ring-emerald-400/60",
        check: "text-emerald-300",
    },
];

const STEP_KEYS = [
    "Mvc_inaction_step1",
    "Mvc_inaction_step2",
    "Mvc_inaction_step3",
];

export default function MvcInAction() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [step, setStep] = useState(0); // 0 repos, 1 ctrl, 2 model, 3 view, 4 page
    const timers = useRef([]);

    function clearTimers() {
        timers.current.forEach((id) => clearTimeout(id));
        timers.current = [];
    }
    useEffect(() => clearTimers, []);

    function play() {
        if (step !== 0 && step !== 4) return;
        clearTimers();
        setStep(1);
        const s = (ms) => (reduce ? ms * 0.3 : ms);
        timers.current.push(setTimeout(() => setStep(2), s(950)));
        timers.current.push(setTimeout(() => setStep(3), s(1900)));
        timers.current.push(setTimeout(() => setStep(4), s(2850)));
    }

    function replay() {
        clearTimers();
        setStep(0);
        timers.current.push(setTimeout(play, 60));
    }

    const progress = Math.min(step, 3) / 3;

    return (
        <section id="mvc-in-action" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Mvc_inaction_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Mvc_inaction_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-slate-400/15 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="layers-minimalistic-bold-duotone"
                            className="w-3.5 h-3.5 text-slate-300"
                        />
                        /articles
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Mvc_inaction_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8">
                    {/* Demande entrante */}
                    <div className="flex justify-center mb-5">
                        <span
                            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12px] font-medium ring-1 transition-colors duration-300 ${
                                step >= 1
                                    ? "bg-slate-500/15 ring-slate-400/40 text-slate-200"
                                    : "bg-white/[0.04] ring-white/10 text-gray-400"
                            }`}
                        >
                            <SolarIcon
                                name="cursor-bold"
                                className="w-3.5 h-3.5"
                            />
                            {t("Mvc_inaction_request_label")}
                        </span>
                    </div>

                    {/* 3 couches */}
                    <div className="flex flex-col md:flex-row md:items-stretch gap-2 sm:gap-3">
                        {LAYERS.map((layer, i) => {
                            const active = step === i + 1;
                            const done = step > i + 1;
                            const on = active || done;
                            return (
                                <Fragment key={layer.key}>
                                    <motion.div
                                        animate={
                                            active && !reduce
                                                ? { scale: [1, 1.03, 1] }
                                                : { scale: 1 }
                                        }
                                        transition={{ duration: 0.5 }}
                                        className={`flex-1 rounded-2xl p-4 ring-1 transition-colors duration-300 ${
                                            active
                                                ? `bg-white/[0.07] ring-2 ${layer.ring}`
                                                : done
                                                  ? "bg-white/[0.05] ring-white/15"
                                                  : "bg-white/[0.02] ring-white/10 opacity-60"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2.5">
                                            <span
                                                className={`flex items-center justify-center w-10 h-10 rounded-xl ${layer.tile}`}
                                            >
                                                <SolarIcon
                                                    name={layer.icon}
                                                    className="w-5 h-5"
                                                />
                                            </span>
                                            {done && (
                                                <SolarIcon
                                                    name="check-circle-bold"
                                                    className={`w-5 h-5 ${layer.check}`}
                                                />
                                            )}
                                        </div>
                                        <div
                                            className={`text-sm font-bold ${on ? "text-white" : "text-gray-400"}`}
                                        >
                                            {t(layer.nameKey)}
                                        </div>
                                        <div
                                            className={`mt-0.5 text-[12.5px] leading-snug ${on ? "text-gray-300" : "text-gray-500"}`}
                                        >
                                            {t(layer.roleKey)}
                                        </div>
                                    </motion.div>
                                    {i < LAYERS.length - 1 && (
                                        <div className="flex items-center justify-center">
                                            <SolarIcon
                                                name="alt-arrow-right-bold"
                                                className={`w-5 h-5 rotate-90 md:rotate-0 transition-colors duration-300 ${
                                                    step > i + 1
                                                        ? "text-slate-300"
                                                        : "text-white/20"
                                                }`}
                                            />
                                        </div>
                                    )}
                                </Fragment>
                            );
                        })}
                    </div>

                    {/* Barre de progression */}
                    <div className="mt-5 h-1 rounded-full bg-white/10 overflow-hidden">
                        <motion.div
                            className="h-full rounded-full bg-gradient-to-r from-amber-400 via-sky-400 to-emerald-400"
                            initial={false}
                            animate={{ scaleX: progress }}
                            style={{ transformOrigin: "left" }}
                            transition={{ duration: reduce ? 0 : 0.6, ease: "easeOut" }}
                        />
                    </div>

                    {/* Statut + action */}
                    <div className="mt-5 flex flex-col items-center text-center">
                        <div className="h-6">
                            <AnimatePresence mode="wait">
                                {step >= 1 && step <= 3 && (
                                    <motion.p
                                        key={step}
                                        initial={reduce ? false : { opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        transition={{ duration: 0.25 }}
                                        className="text-sm font-medium text-gray-200 mb-0"
                                    >
                                        {t(STEP_KEYS[step - 1])}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </div>
                        {step === 0 && (
                            <button
                                type="button"
                                onClick={play}
                                className="mt-1 inline-flex items-center gap-2 rounded-xl bg-slate-600 hover:bg-slate-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/40 transition active:scale-95"
                            >
                                <SolarIcon name="play-bold" className="w-4 h-4" />
                                {t("Mvc_inaction_cta")}
                            </button>
                        )}
                        {step === 4 && (
                            <button
                                type="button"
                                onClick={replay}
                                className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3.5 py-1.5 text-[13px] text-gray-300 transition active:scale-95"
                            >
                                <SolarIcon
                                    name="restart-bold-duotone"
                                    className="w-3.5 h-3.5"
                                />
                                {t("Mvc_inaction_replay")}
                            </button>
                        )}
                    </div>

                    {/* Page produite + révélation */}
                    <AnimatePresence>
                        {step >= 4 && (
                            <motion.div
                                initial={reduce ? false : { opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                className="mt-8 grid md:grid-cols-2 gap-5 items-center"
                            >
                                <div className="flex flex-col items-center">
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 mb-2">
                                        {t("Mvc_inaction_result")}
                                    </span>
                                    <motion.div
                                        initial={reduce ? false : { scale: 0.94, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: "spring", stiffness: 220, damping: 20 }}
                                        className="w-full max-w-[320px] rounded-xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/10 overflow-hidden"
                                    >
                                        <div className="h-10 bg-slate-800 flex items-center px-4">
                                            <span className="text-white text-sm font-bold">
                                                {t("Mvc_inaction_page_title")}
                                            </span>
                                        </div>
                                        <div className="p-4 flex flex-col gap-2.5">
                                            {[0, 1, 2].map((r) => (
                                                <div
                                                    key={r}
                                                    className="flex items-center gap-3"
                                                >
                                                    <span className="w-9 h-9 rounded-md bg-emerald-100 shrink-0" />
                                                    <div className="flex-1 min-w-0 space-y-1.5">
                                                        <div className="h-2 rounded-full bg-slate-200 w-3/4" />
                                                        <div className="h-2 rounded-full bg-slate-100 w-1/2" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                </div>

                                <AnimatePresence>
                                    {step >= 4 && (
                                        <motion.div
                                            initial={reduce ? false : { opacity: 0, x: 12 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: 0.15 }}
                                            className="flex items-start gap-3 rounded-2xl bg-slate-500/10 ring-1 ring-slate-400/30 p-5"
                                        >
                                            <SolarIcon
                                                name="lightbulb-bolt-bold-duotone"
                                                className="w-6 h-6 text-slate-300 shrink-0 mt-0.5"
                                            />
                                            <p className="text-sm text-gray-200 leading-relaxed mb-0">
                                                {t("Mvc_inaction_reveal")}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Mvc_inaction_caption")}
            </p>
        </section>
    );
}
